import { moduleState, state } from 'cerebral'
import { DateTime } from 'luxon'
import * as rrule from 'rrule'
const { RRule, RRuleSet } = rrule
import { compose, find as rFind, pick, propEq, omit, pluck, sortBy, prop, toPairs, uniqBy } from 'ramda'
import apiClient from '../../lib/util/api-client'
import { crudActions } from '../factories'
import { DesignHuddleClient } from '../../lib/util/designhuddle'
import { upload } from '../../lib/util/cloudinary'
import { captureException } from '../../lib/util/errors'
import { getPaperSize } from '../../lib/util/calendar-print/print-paper-sizes'
import utils from '../../../shared/utils'

const { sleep } = utils

const holidaysService = apiClient.service('events/holidays')
const themesService = apiClient.service('calendar-themes')
const diningRdService = apiClient.service('meals/diningrd')
const menuParseService = apiClient.service('meals/parse-menu')

export const { find, setList, clearList, get, save, remove } = crudActions({ name: 'events' })

const timeParts = pick(['hour', 'minute', 'second'])
const getThemeTypes = compose(sortBy(prop('name')), uniqBy(prop('_id')), pluck('type'))

export const togglePublished = async ({ props: { id }, eventsService: service, get }) => {
  if (id) {
    const event = get(moduleState`entities.${id}`)
    const data = await service.patch(id, { ...pick(['startAt', 'endAt'], event), published: !event.published })
    return { entities: { events: [data] } }
  }
}

export const setEditRangeVisible = ({ props: { visible }, store }) => {
  store.set(moduleState`editRangeVisible`, visible)
}

export const setCalendarState = ({ props: { initialDate, initialView }, get, store }) => {
  const calendarState = get(moduleState`calendar`)
  store.set(moduleState`calendar`, { ...calendarState, initialDate, initialView })
}

export const findHolidays = async ({ props: { query } }) => {
  const holidays = await holidaysService.find({ query })
  holidays.forEach((holiday) => {
    holiday.color = '#000000'
    holiday.editable = false
    holiday.allDay = true
  })
  return { holidays }
}

export const fetchDiningRdMenus = async () => {
  const menus = await diningRdService.find()
  return { menus }
}

export const fetchMenuEventsForDate = async ({ props: { date }, eventsService, get }) => {
  const timezone = get(state`account.currentUser.timezone`)
  const dayStart = DateTime.fromISO(date, { zone: timezone }).startOf('day').toJSDate()
  const dayEnd = DateTime.fromISO(date, { zone: timezone }).endOf('day').toJSDate()
  const meals = await eventsService.find({ eventType: 'Menu', startAt: { $gte: dayStart, $lte: dayEnd }, $limit: 100, $sort: { mealLocation: 1, startAt: 1 } })
  return { menuEvents: meals.data || meals || [] }
}

export const getDiningRdMenuMeals = async ({ props: { menuId } = {}, store }) => {
  store.set(moduleState`upload.loading`, true)
  try {
    const meals = await diningRdService.create({ menuId })
    store.set(moduleState`upload.loading`, false)
    return meals
  } catch (error) {
    store.set(moduleState`upload.loading`, false)
    store.set(
      moduleState`upload.error`,
      `We encountered an unexpected error importing these meals. Please try again, or contact us to let us know you're having trouble.`
    )
    throw error
  }
}

// Two minutes
const POLL_TIMEOUT = 2 * 60 * 1000

export const parseMenuDocx = async ({ props: { file, filename, templateType, startDate } = {}, store }) => {
  store.set(moduleState`upload.loading`, true)

  try {
    const { jobId } = await menuParseService.create({ file, filename, templateType, startDate })
    const pollExpireTime = Date.now() + POLL_TIMEOUT

    while (Date.now() < pollExpireTime) {
      const result = await menuParseService.get(jobId, { query: { templateType } })

      if (result.status === 'success') {
        store.set(moduleState`upload.loading`, false)
        return result
      }

      await sleep(3000)
    }

    throw new Error('Timed out waiting for the import menu parsing.')
  } catch (error) {
    const message = "We were not able to read the format of the provided file. Please try again, or contact us to let us know you're having trouble."
    store.set(moduleState`upload.loading`, false)
    store.set(moduleState`upload.error`, message)
    throw error
  }
}

export const findThemes = async ({ props: { query = {} }, store }) => {
  const { data: themes = [] } = await themesService.find({ query })
  const types = getThemeTypes(themes)
  store.set(moduleState`calendarThemeTypes`, types)
  store.set(moduleState`calendarThemes`, themes)
}

const setPrintOption = (name) => ({ props: { value }, store }) => {
  store.set(moduleState`calendarPrint.${name}`, value)
}

export const setPaperSizeName = setPrintOption('paperSizeName')

export const applyPaperSizeDefaults = ({ props: { value }, get, store }) => {
  const previous = getPaperSize(get(moduleState`calendarPrint.paperSizeName`))
  const next = getPaperSize(value)
  const setOption = ([name, optionValue]) => store.set(moduleState`calendarPrint.${name}`, optionValue)

  if (previous.defaults) {
    toPairs(previous.defaults).forEach(([name]) => setOption([name, false]))
  }

  if (next.defaults) {
    toPairs(next.defaults).forEach(setOption)
  }
}

export const setFontSizeName = setPrintOption('fontSizeName')
export const setPaperOrientation = setPrintOption('paperOrientation')
export const setPrintSingleLine = setPrintOption('printSingleLine')
export const setHideEventDescription = setPrintOption('hideEventDescription')
export const setEventTypeHidden = setPrintOption('hideEventType')
export const setEventLocationHidden = setPrintOption('hideEventLocation')
export const setBirthdaysShown = setPrintOption('showBirthdays')
export const setEventCalendars = setPrintOption('eventCalendars')
export const setWeeklyCalendarLayout = setPrintOption('weeklyCalendarLayout')
export const setDayPrintInterval = setPrintOption('dayPrintInterval')
export const setLevelsOfCare = setPrintOption('levelsOfCare')
export const setTransportRequiredOnly = setPrintOption('showTransportRequiredOnly')
export const setEndTimeHidden = setPrintOption('hideEndTime')
export const setExpandToFillPage = setPrintOption('expandToFillPage')
export const setIncludeMenu = setPrintOption('includeMenu')

export const selectCalendarTheme = ({ props: { id }, get, store }) => {
  const theme = rFind(propEq(id, '_id'), get(moduleState`calendarThemes`))
  store.set(moduleState`calendarPrint.theme`, theme || null)
}

export const changeEventTime = async ({ props: { _id, originStart, delta, editing = {} }, get, eventsService }) => {
  const timezone = get(state`account.currentUser.timezone`)
  const event = get(state`events.entities.${_id}`)
  originStart = DateTime.fromMillis(originStart, { zone: timezone })
  // eslint-disable-next-line no-useless-assignment
  let { startAt, endAt, repeatType } = event
  const originEnd = originStart.set(timeParts(DateTime.fromJSDate(endAt, { zone: timezone })))

  // Only modify single occurrences or events which do not repeat.
  if (!repeatType || repeatType === 'none' || editing.range === 'this') {
    startAt = originStart.plus(delta)
    endAt = originEnd.plus(delta)
    if (editing.range === 'this') {
      editing.instance = originStart.toISODate()
      editing.targetInstance = startAt.toISODate()
    }
    const result = await eventsService.patch(_id, { ...event, startAt, endAt, editing })
    return { values: result }
  }
}

export const mapCalendarFields = ({ props: { entities }, get }) => {
  const timezone = get(state`account.currentUser.timezone`)
  const labels = get(state`account.eventLabels`)
  const eventLocationCode = get(state`account.eventLocationCode`)

  let { events } = entities
  const typeCache = {}
  const locations = {}

  events = events.map((event) => {
    let { _id: id, title, locationShortCode, startAt, endAt, eventType, eventLabelRef, allDay, repeatRule, repeatRuleExceptDates } = event
    startAt = DateTime.fromJSDate(startAt).setZone(timezone)
    endAt = DateTime.fromJSDate(endAt).setZone(timezone)

    if (eventLocationCode && locationShortCode) {
      if (event.location) {
        locations[locationShortCode] = event.location
      }
      event = { ...event, title: `${title} [${locationShortCode}]` }
    }

    // Leave out irrelevant fields or defaults from schema casting.
    event = { ...omit(['rrule', 'startAt', 'endAt', 'repeatRuleExceptDates'], event), id }
    const start = allDay ? startAt.toISODate() : startAt.toISO()

    // End date is exclusive. See https://fullcalendar.io/docs/event-object
    const end = allDay ? endAt.plus({ day: 1 }).toISODate() : endAt.toISO()
    event = { ...event, startISO: start, start, end }

    if (repeatRule) {
      event.rrule = RRule.parseString(repeatRule)
      // RRule.js works best with UTC dates.
      event.rrule.dtstart = startAt.setZone('utc', { keepLocalTime: true }).toJSDate()
      event.rrule.until = endAt.endOf('day').setZone('utc', { keepLocalTime: true }).toJSDate()

      // If there are any exceptions, add those to our handling of repeating dates.
      if (repeatRuleExceptDates) {
        const exceptions = repeatRuleExceptDates
          .map((date) => DateTime.fromJSDate(date).setZone('utc').set(timeParts(startAt)).toJSDate())
          .filter((date) => date >= startAt && date <= endAt)
        if (exceptions.length > 0) {
          const rruleSet = new RRuleSet()
          rruleSet.rrule(
            new RRule({
              ...event.rrule,
              dtstart: startAt.setZone('utc', { keepLocalTime: true }).toJSDate(),
              until: endAt.setZone('utc', { keepLocalTime: true }).toJSDate(),
            })
          )
          exceptions.forEach((date) => rruleSet.exdate(date))
          // FullCalendar cannot process exceptions, unless in string form.
          event.rrule = rruleSet.toString()
        }
      }
      event.duration = allDay ? { days: 1 } : pick(['hours', 'minutes'], endAt.diff(startAt, ['days', 'hours', 'minutes']))
    }

    // Set event color based on the resort settings.
    const labelKey = `${eventType}:${eventLabelRef}`
    const typeLabels = labels[eventType]

    let found = typeCache[labelKey]
    if (!found && typeLabels) {
      found = rFind(propEq(eventLabelRef, '_id'), typeLabels)
    }

    if (found) {
      event.color = found.color
      event.labelColor = found.color
      event.labelTextColor = found.textColor
      event.shortCode = found.shortCode
    }
    return event
  })
  return { events, locations }
}

export const resetUploadState = ({ store }) => {
  store.set(moduleState`upload`, { created: 0, updated: 0, success: null, error: '' })
}

export const setUploadErrorMessage = ({ props: { message }, store }) => {
  store.set(moduleState`upload.error`, message)
}

export const create = async ({ props: { data, entities = {} }, store, eventsService }) => {
  store.set(moduleState`upload.loading`, true)
  try {
    const { created, updated, data: events } = await eventsService.create(data)
    store.merge(moduleState`upload`, { created, updated, success: true, loading: false })
    return { entities: { ...entities, events } }
  } catch (error) {
    store.set(moduleState`upload.loading`, false)
    store.set(
      moduleState`upload.error`,
      `We encountered an unexpected error importing these meals. Please try again, or contact us to let us know you're having trouble.`
    )
    throw error
  }
}

export const getBirthdays = async ({ props: { start, end }, profilesService, store, get }) => {
  const currentUser = get(state`account.currentUser`)
  const packages = get(state`account.packages`)

  if (currentUser?.canAccessCommandCenter && packages?.people) {
    const { data: birthdays = [] } = await profilesService.find({ filter: 'birthdaysForTimeRange', start, end, $limit: 50 })
    store.set(moduleState`birthdays`, birthdays)
  }
}

export const saveImage = async ({ props, app, eventsService, store }) => {
  const getEditorToken = app.getSequence(`publications.getEditorToken`)
  const signUpload = app.getSequence('uploads.signUpload')
  const showFeedback = app.getSequence('feedback.showFeedback')
  const { entities = {}, values: { editableLayout: { projectId: editorProjectId } = {}, updateDownloadableImage = false } = {} } = props
  const { events: [{ _id, title, ...rest } = {}] = [] } = entities

  if (editorProjectId && updateDownloadableImage) {
    try {
      store.set(moduleState`image.generating`, true)
      const { access_token } = await getEditorToken()
      const designHuddleClient = new DesignHuddleClient(access_token)
      const eventImage = await designHuddleClient.exportProject(editorProjectId, title, 'png')

      if (!eventImage?.download_url) {
        throw new Error('Design Huddle export finished without a download url.')
      }

      let event = {}

      if (eventImage) {
        const blob = await fetch(eventImage.download_url).then((res) => res.blob())
        const image = await upload(blob, { signUpload, uploadPreset: 'editor-pdf', publicId: _id })
        event = await eventsService.patch(_id, { ...rest, image })
      }
      store.set(moduleState`image.generating`, false)
      return { entities: { ...entities, events: [event] } }
    } catch (error) {
      store.set(moduleState`image.generating`, false)
      showFeedback({ title: 'Image Generation Failed', body: 'There was a problem while generating the event image', type: 'error' })
      captureException(error)
    }
  }
}


const setSaveOption = (name) => ({ props: { value }, store }) => {
  store.set(moduleState`saveOptions.${name}`, value)
}

export const setSaveAction = setSaveOption('action')
